import type { PlaySignupCtaTrigger } from '~/composables/usePlaySignupCta'

// 3 体験 (写経 / 制作 / 遊び) の回遊計測用 GA4 イベント。
// 設計の詳細は doc/architecture/analytics.md を参照。
// イベントに `path` パラメータは付けない (体験帰属を決め打ちしない)。
export function useFunnelEvent() {
  const { gtag } = useGtag()

  const trackAreaVisit = (areaId: string) => {
    gtag('event', 'area_visit', { area_id: areaId })
  }

  const trackWonderClear = (sceneId: string, areaId: string) => {
    gtag('event', 'wonder_clear', { scene_id: sceneId, area_id: areaId })
  }

  const trackAreaClear = (areaId: string) => {
    gtag('event', 'area_clear', { area_id: areaId })
  }

  const trackGrimoireCreate = (projectType: string) => {
    gtag('event', 'grimoire_create', { project_type: projectType })
  }

  const trackCodeRun = (projectType: string) => {
    gtag('event', 'code_run', { project_type: projectType })
  }

  const trackCrystallize = (workSlug: string) => {
    gtag('event', 'crystallize', { work_slug: workSlug })
  }

  const trackWorkPlay = (workSlug: string, isOwn: boolean) => {
    gtag('event', 'work_play', { work_slug: workSlug, is_own: isOwn })
  }

  const trackWorkLike = (workSlug: string) => {
    gtag('event', 'work_like', { work_slug: workSlug })
  }

  const trackGiftRedeem = (target: string) => {
    gtag('event', 'gift_redeem', { target })
  }

  // センパイの自発ヒント (#745)。表示 / クリック / OFF を 1 イベントの action で分ける。
  // クリック率は shown と clicked の比、うるささの proxy は disabled の比率で見る。
  const trackSenpaiHint = (action: 'shown' | 'clicked' | 'disabled', hintId?: string) => {
    gtag('event', 'senpai_hint', { action, hint_id: hintId ?? 'none' })
  }

  // #860: 新規登録の完了。GA4 のキーイベントで、「流入元別の登録転換率」の分子になる。
  // **必ずブラウザから送る** —— サーバー側 Measurement Protocol だと `_ga` cookie を
  // 伝播していないためブラウザ側セッションと結合できず、肝心の流入元帰属が失われる
  // (`purchase` が実際にそうなっている。doc/architecture/analytics.md 参照)。
  // `method` は GA4 の標準ディメンションなのでカスタム定義の登録は不要。
  const trackSignUp = (method: 'email' | 'google') => {
    gtag('event', 'sign_up', { method })
  }

  // #795: 匿名プレイの離脱時サインアップ CTA。表示 / クリック / 見送りを action で分け、
  // trigger で「滞在時間」と「セッション内 2 作品目」のどちらが効いたかを見る。
  // サインアップ完走は同一セッション内の後続 `sign_up` イベントで読む。
  const trackPlaySignupCta = (
    action: 'shown' | 'clicked' | 'dismissed',
    trigger: PlaySignupCtaTrigger,
  ) => {
    gtag('event', 'play_signup_cta', { action, trigger })
  }

  // #844: 「ほかの作品を見る」= 回遊の導線。サインアップ CTA（`play_signup_cta`）とは
  // 別イベントにする —— 求めているものが違う（登録 vs もう 1 本遊ぶ）ので、同じ
  // action に混ぜると「CTA は効いているのか」を後から分離できなくなる。
  // placement: `header` = プレイ面のヘッダー常設リンク / `leave_dialog` = 離脱ダイアログ。
  const trackPlayMoreCta = (
    action: 'shown' | 'clicked',
    placement: 'header' | 'leave_dialog',
  ) => {
    gtag('event', 'play_more_cta', { action, placement })
  }

  // #920: 教室・学校向け LP（/classroom）の計測。B2B は営業を経由しない売り方なので、
  // 「営業なしで何件来るか」はこのイベントでしか分からない。
  // action: `entry` = サイト内の導線から LP へ入った / `clicked` = LP の問い合わせ CTA を押した。
  // location: 導線の置き場所（`footer` / `top` / `pricing`）、CTA は `lp`。
  // ⚠️ 判断に使えるのは実際の問い合わせ件数（フォーム側）で、これはその手前の 2 段
  //    —— 「宣伝が届いたか」と「文面が効いたか」の切り分けにだけ使う。
  const trackClassroomCta = (
    action: 'entry' | 'clicked',
    location: 'footer' | 'top' | 'pricing' | 'lp',
  ) => {
    gtag('event', 'classroom_cta', { action, location })
  }

  return {
    trackSignUp,
    trackAreaVisit,
    trackWonderClear,
    trackAreaClear,
    trackGrimoireCreate,
    trackCodeRun,
    trackCrystallize,
    trackWorkPlay,
    trackWorkLike,
    trackGiftRedeem,
    trackSenpaiHint,
    trackPlaySignupCta,
    trackPlayMoreCta,
    trackClassroomCta,
  }
}
